import { ConfirmButton } from "@dashboard/components/ConfirmButton";
import { DashboardModal } from "@dashboard/components/Modal";
import Money from "@dashboard/components/Money";
import PriceField from "@dashboard/components/PriceField";
import { DiscountValueTypeEnum, OrderLineFragment } from "@dashboard/graphql";
import { buttonMessages } from "@dashboard/intl";
import {
  OrderLineDiscountContextConsumerProps,
  useOptionalOrderLineDiscountContext,
} from "@dashboard/products/components/OrderDiscountProviders/OrderLineDiscountProvider";
import { Box, Button, Input, Text } from "@saleor/macaw-ui-next";
import { ChangeEvent, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { messages } from "./messages";

type Mode = "price" | "percentage";

const round2 = (value: number) => Math.round(value * 100) / 100;

interface OrderLineDiscountFormProps {
  line: OrderLineFragment;
  discount: OrderLineDiscountContextConsumerProps;
}

// One-off price change for a single order line. "Unit price" is sent as a
// fixed per-unit discount (original − new price) and "Discount %" as a
// percentage discount; the product's catalogue price is never touched.
const OrderLineDiscountForm = ({ line, discount }: OrderLineDiscountFormProps) => {
  const intl = useIntl();
  const original = line.undiscountedUnitPrice.gross;
  const existingType = discount.orderLineDiscount?.calculationMode;
  const hasDiscount = !!discount.orderLineDiscount?.value;
  const [mode, setMode] = useState<Mode>(
    existingType === DiscountValueTypeEnum.PERCENTAGE ? "percentage" : "price",
  );
  const [price, setPrice] = useState(String(line.unitPrice.gross.amount));
  const [percentage, setPercentage] = useState(
    existingType === DiscountValueTypeEnum.PERCENTAGE
      ? String(discount.orderLineDiscount?.value ?? 0)
      : "0",
  );
  const [reason, setReason] = useState(discount.orderLineDiscount?.reason ?? "");

  const priceValue = parseFloat(price);
  const percentageValue = parseFloat(percentage);
  const priceInvalid =
    mode === "price" && (isNaN(priceValue) || priceValue < 0 || priceValue > original.amount);
  const percentageInvalid =
    mode === "percentage" &&
    (isNaN(percentageValue) || percentageValue < 0 || percentageValue > 100);
  const resultingUnit =
    mode === "price"
      ? round2(priceValue)
      : round2(original.amount * (1 - (percentageValue || 0) / 100));

  const handleSubmit = () => {
    if (mode === "price") {
      discount.addOrderLineDiscount({
        calculationMode: DiscountValueTypeEnum.FIXED,
        value: round2(original.amount - priceValue),
        reason,
      });
    } else {
      discount.addOrderLineDiscount({
        calculationMode: DiscountValueTypeEnum.PERCENTAGE,
        value: percentageValue,
        reason,
      });
    }
  };

  return (
    <Box display="grid" gap={4}>
      <Text size={2} color="default2">
        <FormattedMessage
          {...messages.originalPrice}
          values={{ price: <Money money={original} /> }}
        />
      </Text>
      <Box display="flex" gap={2}>
        {(["price", "percentage"] as Mode[]).map(option => (
          <Button
            key={option}
            type="button"
            variant={mode === option ? "primary" : "secondary"}
            data-test-id={`line-discount-mode-${option}`}
            onClick={() => setMode(option)}
          >
            {intl.formatMessage(option === "price" ? messages.modePrice : messages.modePercentage)}
          </Button>
        ))}
      </Box>
      {mode === "price" ? (
        <PriceField
          label={intl.formatMessage(messages.newPriceLabel)}
          value={price}
          currencySymbol={original.currency}
          error={priceInvalid}
          hint={priceInvalid ? intl.formatMessage(messages.priceTooHigh) : ""}
          onChange={(event: ChangeEvent<HTMLInputElement>) => setPrice(event.target.value)}
          data-test-id="line-discount-price"
        />
      ) : (
        <PriceField
          label={intl.formatMessage(messages.percentageLabel)}
          value={percentage}
          currencySymbol="%"
          error={percentageInvalid}
          hint={percentageInvalid ? intl.formatMessage(messages.percentageInvalid) : ""}
          onChange={(event: ChangeEvent<HTMLInputElement>) => setPercentage(event.target.value)}
          data-test-id="line-discount-percentage"
        />
      )}
      <Input
        label={intl.formatMessage(messages.reasonLabel)}
        value={reason}
        onChange={(event: ChangeEvent<HTMLInputElement>) => setReason(event.target.value)}
        data-test-id="line-discount-reason"
      />
      {!priceInvalid && !percentageInvalid && (
        <Text size={3}>
          <FormattedMessage
            {...messages.resultingPrice}
            values={{
              price: <Money money={{ amount: resultingUnit, currency: original.currency }} />,
              total: (
                <Money
                  money={{
                    amount: round2(resultingUnit * line.quantity),
                    currency: original.currency,
                  }}
                />
              ),
            }}
          />
        </Text>
      )}
      <Text size={2} color="default2">
        {intl.formatMessage(messages.hint)}
      </Text>
      <DashboardModal.Actions>
        {hasDiscount && (
          <ConfirmButton
            variant="error"
            transitionState={discount.orderLineDiscountRemoveStatus}
            onClick={discount.removeOrderLineDiscount}
            data-test-id="line-discount-remove"
          >
            {intl.formatMessage(buttonMessages.remove)}
          </ConfirmButton>
        )}
        <ConfirmButton
          variant="primary"
          disabled={priceInvalid || percentageInvalid}
          transitionState={discount.orderLineDiscountUpdateStatus}
          onClick={handleSubmit}
          data-test-id="line-discount-submit"
        >
          {intl.formatMessage(buttonMessages.save)}
        </ConfirmButton>
      </DashboardModal.Actions>
    </Box>
  );
};

interface OrderLineDiscountDialogProps {
  lines: OrderLineFragment[];
}

// Renders the price/discount dialog for whichever line had it opened through
// the OrderLineDiscountProvider (row menu "Edit price / discount").
export const OrderLineDiscountDialog = ({ lines }: OrderLineDiscountDialogProps) => {
  const intl = useIntl();
  const getLineDiscount = useOptionalOrderLineDiscountContext();

  if (!getLineDiscount) {
    return null;
  }

  const line = lines.find(({ id }) => getLineDiscount(id).isDialogOpen);
  const discount = line ? getLineDiscount(line.id) : null;

  return (
    <DashboardModal open={!!line} onChange={() => discount?.closeDialog()}>
      <DashboardModal.Content size="sm">
        {line && discount && (
          <>
            <DashboardModal.Header>
              {intl.formatMessage(messages.title, { productName: line.productName })}
            </DashboardModal.Header>
            <OrderLineDiscountForm key={line.id} line={line} discount={discount} />
          </>
        )}
      </DashboardModal.Content>
    </DashboardModal>
  );
};
